import { useEffect, useRef } from "react";

import fireConversion from "@/features/tracking/registry/fireConversion";
import { type FireConversionParams } from "@/features/tracking/registry/fireConversion.types";

/**
 * Fire a conversion exactly once per component mount, optionally
 * gated by a boolean state transition.
 *
 * Two common call-site shapes this covers:
 * - **Mount-fire** (no `when` — defaults to `true`): fires on first
 *   effect run. Use case: paywall / pricing page view.
 * - **State-transition fire** (`when` boolean): holds until `when`
 *   flips to `true`, fires exactly once, then stays quiet for the
 *   rest of the mount regardless of further `when` toggles. Use
 *   case: "checkout is ready to submit" (price calculated, options
 *   loaded, plan switched finished).
 *
 * Uses a ref-held `paramsBuilder` callback — not a plain params
 * object — for two reasons:
 * - The builder runs at fire-time, not render-time, so side-effecty
 *   constructions like `generateConversionEventId()` aren't
 *   wastefully recomputed on every render.
 * - Avoids the footgun of a caller passing a freshly-constructed
 *   params object every render and wondering why `useEffect` deps
 *   keep invalidating.
 *
 * **Not** a replacement for inline `fireConversion` calls that
 * co-fire with other analytics (e.g. a GA4 `logEvent` guarded by
 * the same ref): extracting just the Meta half into this hook would
 * put two ref latches on the same conceptual event and invite
 * desync. Leave those inline.
 */
export default function useFireConversionOnce(paramsBuilder: () => FireConversionParams, when: boolean = true): void {
  const refFired = useRef(false);
  const refParamsBuilder = useRef(paramsBuilder);
  // Keep the ref in sync so the fire (which may happen on a later
  // render when `when` flips true) uses the latest closure, not the
  // one captured on mount.
  refParamsBuilder.current = paramsBuilder;

  useEffect(() => {
    if (!when || refFired.current) {
      return;
    }
    refFired.current = true;
    fireConversion(refParamsBuilder.current());
  }, [when]);
}
